/** NestJS / axios error body */
type ApiErrorBody = {
  message?: string | string[];
  error?: string;
  statusCode?: number;
  /** Machine-readable code (backend-v2/src/common/errors/app-errors.ts, PRISMA_*) */
  code?: string;
  details?: Record<string, unknown>;
};

/**
 * RU overrides for codes whose backend message is English/technical (Prisma filter).
 * Domain codes (ROUND_*, ORDER_*, COST_*, QTY_*) already carry Russian messages —
 * for them the backend message wins.
 */
const CODE_MESSAGES: Record<string, string> = {
  PRISMA_P2002: 'Такая запись уже есть. Проверьте, нет ли дубля.',
  PRISMA_P2003: 'Связанная запись не найдена. Обновите страницу и повторите.',
  PRISMA_P2025: 'Запись не найдена — возможно, её уже удалили.',
  PRISMA_P2000: 'Слишком длинное значение в одном из полей.',
};

const EXACT_EN: Record<string, string> = {
  'Access verification failed': 'Не удалось проверить доступ. Обновите страницу.',
  'You do not have permission to access this resource': 'Нет доступа к этой записи.',
  Forbidden: 'Недостаточно прав для этого действия.',
  Unauthorized: 'Сессия истекла. Войдите снова.',
  'Database error': 'Не удалось обработать данные. Повторите позже; если ошибка повторяется, сообщите администратору.',
  'Network Error': 'Нет связи с сервером. Проверьте подключение и повторите.',
  'Invalid credentials': 'Неверный логин или пароль. Проверьте данные и повторите.',
  'timeout exceeded': 'Сервер не ответил вовремя. Повторите попытку.',
  'Bad Request': 'Запрос отклонён. Проверьте поля и повторите.',
  'Internal Server Error': 'Сбой сервера. Повторите через минуту.',
  'Not Found': 'Запись не найдена.',
  'City order not found': 'Заявка не найдена.',
  'Round not found': 'Рейс не найден.',
  'Stop not found': 'Остановка рейса не найдена.',
  'Leg not found': 'Плечо рейса не найдено.',
  'Client not found': 'Клиент не найден.',
  'Client not found in this firm': 'Клиент не найден в этой фирме.',
  'Regional representative not found': 'Исполнитель (РП) не найден.',
  'Regional representative not found in this firm': 'Исполнитель (РП) не найден в этой фирме.',
  'City round not found': 'Городской рейс не найден.',
  'Order not found': 'Заявка не найдена.',
  'Incident not found': 'Инцидент не найден.',
  'Agent not found': 'Агент не найден.',
  'City not found': 'Город не найден.',
  'Representative not found': 'Исполнитель (РП) не найден.',
  'Cannot delete client with active orders': 'Нельзя удалить клиента: есть активные заявки.',
  'Cannot remove last stop from leg': 'Нельзя удалить последнюю остановку маршрута.',
  'Failed to update agent': 'Не удалось сохранить агента.',
  'Invalid roleId': 'Выберите существующую роль.',
  'Invalid foreign key reference': 'Ссылка на несуществующую запись. Обновите страницу.',
  'kind, refType, refId, caseKey required': 'Не хватает данных для операции.',
  'caseKey required': 'Не хватает ключа операции.',
  'dateFrom and dateTo are required': 'Укажите период: дата «с» и дата «по».',
  'Invalid dateFrom/dateTo': 'Некорректный период дат.',
  'clientId is required': 'Выберите клиента.',
  'clientId, dateFrom and dateTo are required': 'Укажите клиента и период.',
  'name is required': 'Укажите название.',
  'columns must be an array': 'Список колонок задан неверно.',
  'rows must be an array': 'Список строк задан неверно.',
};

const PATTERNS: Array<[RegExp, string]> = [
  [/^Regional order .+ not found$/i, 'Заявка не найдена.'],
  [/^City order .+ not found$/i, 'Заявка не найдена.'],
  [/^Branch .+ not found$/i, 'Филиал не найден.'],
  [/^Cargo item .+ not found$/i, 'Место груза не найдено.'],
  [/^ClientPayment .+ not found$/i, 'Платёж не найден.'],
  [/^PaymentRecord .+ not found$/i, 'Платёж не найден.'],
  [/^Tarif .+ not found$/i, 'Тариф не найден.'],
  [/^Firm .+ not found$/i, 'Фирма не найдена.'],
  [/^Driver .+ not found$/i, 'Водитель не найден.'],
  [/^Transport .+ not found$/i, 'Транспорт не найден.'],
  [/^TransportType .+ not found$/i, 'Тип транспорта не найден.'],
  [/^Carrier .+ not found$/i, 'Перевозчик не найден.'],
  [/^City .+ not found$/i, 'Город не найден.'],
  [/^Message .+ not found$/i, 'Сообщение не найдено.'],
  [/^Cost .+ not found$/i, 'Статья расхода не найдена.'],
  [/^CostType .+ not found$/i, 'Тип расхода не найден.'],
  [/^AcceptanceReport .+ not found$/i, 'Акт приёмки не найден.'],
  [/^AdditionalService .+ not found$/i, 'Доп. услуга не найдена.'],
  [/^Service item .+ not found$/i, 'Услуга не найдена.'],
  [/^BoxProperty .+ not found$/i, 'Тип короба не найден.'],
  [/^Courier .+ not found$/i, 'Курьер не найден.'],
  [/^Resolution .+ not found$/i, 'Резолюция не найдена.'],
  [/^TimeRoute .+ not found$/i, 'Срок доставки не найден.'],
  [/^CityTuple .+ not found$/i, 'Правило маршрута не найдено.'],
  [/^Order .+ not found$/i, 'Заявка не найдена.'],
  [/must be a UUID/i, 'Некорректный идентификатор.'],
  [/must be an? (email|Email)/i, 'Укажите корректный email.'],
  [/should not be empty/i, 'Заполните обязательное поле.'],
  [/must be a number/i, 'Укажите число.'],
  [/must be an integer/i, 'Укажите целое число.'],
];


export const ERROR_FIELD_LABELS: Record<string, string> = {
  name: 'Название', fullname: 'Название', username: 'Логин', password: 'Пароль', email: 'Email',
  senderId: 'Отправитель', receiverId: 'Получатель', sBranchId: 'Филиал отправителя', rBranchId: 'Филиал получателя',
  fromCityId: 'Город отправления', toCityId: 'Город назначения', cityId: 'Город',
  carrierId: 'Перевозчик', driverId: 'Водитель', transportId: 'Транспорт', trailerId: 'Прицеп',
  waybillNumber: 'Номер ТТН', orderDatetime: 'Дата предзаказа', plannedShipmentDate: 'Планируемая дата отгрузки',
  fromDatetime: 'Дата отправки', toDatetime: 'Дата погрузки', podDatetime: 'Дата доставки',
  quantity: 'Количество мест', seatsCount: 'Количество мест', weight: 'Вес', physicalWeight: 'Вес',
  volume: 'Объём', volumetricWeight: 'Объёмный вес', length: 'Длина', width: 'Ширина', height: 'Высота',
  clientSum: 'Сумма клиента', execSum: 'Сумма исполнителя', coefficient: 'Коэффициент',
  coefficientReason: 'Причина изменения коэффициента', waybillChangeReason: 'Причина изменения ТТН',
};

function validationText(raw: string): { field: string; message: string } | null {
  const match = raw.match(/^(\w+(?:\.\d+\.\w+)*)\s+(.+)$/);
  if (!match) return null;
  const [,field,rule] = match;
  let message: string | undefined;
  if (/should not be empty|must be defined|required/i.test(rule)) message = 'Заполните обязательное поле.';
  else if (/must be a UUID/i.test(rule)) message = 'Выберите запись из списка.';
  else if (/must be an? email/i.test(rule)) message = 'Введите корректный email.';
  else if (/must be an integer/i.test(rule)) message = 'Введите целое число.';
  else if (/must be a number|must be a numeric/i.test(rule)) message = 'Введите число.';
  else if (/must be a valid ISO 8601 date|must be a Date/i.test(rule)) message = 'Укажите корректные дату и время.';
  else if (/must not be less than|min /i.test(rule)) message = `Укажите значение не меньше ${rule.match(/-?\d+(?:\.\d+)?/)?.[0] ?? 'допустимого минимума'}.`;
  else if (/must not be greater than|max /i.test(rule)) message = `Укажите значение не больше ${rule.match(/-?\d+(?:\.\d+)?/)?.[0] ?? 'допустимого максимума'}.`;
  else if (/must be longer than or equal to/i.test(rule)) message = `Введите не меньше ${rule.match(/\d+/)?.[0]} символов.`;
  else if (/must be shorter than or equal to/i.test(rule)) message = `Введите не больше ${rule.match(/\d+/)?.[0]} символов.`;
  else if (/must be a string/i.test(rule)) message = 'Введите текст.';
  else if (/must be one of|must be a valid enum/i.test(rule)) message = 'Выберите допустимое значение из списка.';
  if (!message) return null;
  return { field, message: `${ERROR_FIELD_LABELS[field.split('.').pop()!] ?? 'Поле'}: ${message}` };
}

/** Only explicit validation fields are mapped. Access/network errors never mark inputs. */
export function extractApiFieldErrors(error: unknown): Record<string, string> {
  const e = error as MaybeAxiosError | null;
  if (e?.apiFieldErrors) return e.apiFieldErrors;
  if (!e?.response || ![400,422].includes(e.response.status ?? 400)) return {};
  const messages = e.response.data?.message;
  const out: Record<string, string> = {};
  for (const raw of Array.isArray(messages) ? messages : typeof messages === 'string' ? [messages] : []) {
    const result = validationText(raw);
    if (result) out[result.field] = result.message;
  }
  return out;
}

export function humanizeErrorText(raw: string): string {
  const text = raw.trim();
  if (!text || /^Request failed with status code \d+$/i.test(text)) return '';

  const validation = validationText(text);
  if (validation) return validation.message;

  const exact = EXACT_EN[text];
  if (exact) return exact;

  for (const [re, ru] of PATTERNS) {
    if (re.test(text)) return ru;
  }

  if (!/[А-Яа-яЁё]/.test(text) && /[a-z]/i.test(text)) return 'Не удалось выполнить действие. Проверьте данные и повторите.';
  if (/Prisma|SQLSTATE|SELECT |INSERT |stack trace|ECONNREFUSED/i.test(text)) return 'Сбой сервера. Повторите позже; если ошибка повторяется, сообщите администратору.';
  return text;
}

function humanizeJoined(parts: string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of parts) {
    const h = humanizeErrorText(part);
    if (!h || seen.has(h)) continue;
    seen.add(h);
    out.push(h);
  }
  return out.join(' ');
}

type MaybeAxiosError = {
  apiErrorMessage?: string;
  apiFieldErrors?: Record<string, string>;
  response?: { data?: ApiErrorBody; status?: number };
  message?: string;
  code?: string;
};

export function extractApiErrorDetails(error: unknown): Record<string, unknown> | null {
  if (!error || typeof error !== 'object') return null;
  const data = (error as MaybeAxiosError).response?.data;
  if (data?.details && typeof data.details === 'object') return data.details;
  return null;
}

export function extractApiErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const data = (error as MaybeAxiosError).response?.data;
  return typeof data?.code === 'string' && data.code ? data.code : null;
}

function statusFallback(status?: number): string | null {
  if (status === 429) return 'Слишком много запросов. Подождите минуту и повторите.';
  if (status === 401) return 'Сессия истекла. Войдите снова.';
  if (status === 403) return 'Недостаточно прав для этого действия.';
  if (status === 404) return 'Запись не найдена.';
  if (status === 409) return 'Конфликт: запись уже изменена. Обновите страницу.';
  if (status === 422) return 'Проверьте заполнение полей и повторите.';
  if (status === 500 || status === 502) return 'Сбой сервера. Повторите через минуту.';
  if (status === 503) return 'Сервис временно недоступен. Повторите позже.';
  return null;
}

/** Extract readable detail from API / network error (no generic fallback). */
export function extractApiErrorDetail(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;

  const e = error as MaybeAxiosError;
  const data = e.response?.data;
  const status = e.response?.status;

  if (typeof data?.code === 'string' && CODE_MESSAGES[data.code]) {
    return CODE_MESSAGES[data.code];
  }

  if (e.apiErrorMessage?.trim()) {
    return humanizeErrorText(e.apiErrorMessage) || e.apiErrorMessage.trim();
  }

  if (data?.message != null) {
    if (Array.isArray(data.message)) {
      const joined = humanizeJoined(data.message.map(String));
      if (joined) return joined;
    } else if (typeof data.message === 'string' && data.message.trim()) {
      const human = humanizeErrorText(data.message);
      if (human) return human;
    }
  }

  if (typeof data?.error === 'string' && data.error.trim()) {
    const human = humanizeErrorText(data.error);
    if (human) return human;
  }

  const byStatus = statusFallback(status);
  if (byStatus) return byStatus;

  if (e.code === 'ERR_NETWORK') {
    return 'Нет связи с сервером. Проверьте сеть и повторите.';
  }

  if (e.message) {
    const human = humanizeErrorText(e.message);
    if (human) return human;
  }

  return null;
}

const FALLBACK = 'Не удалось выполнить действие. Повторите или обновите страницу.';

/**
 * Текст для тоста и баннера.
 * Контекст — что делал пользователь («Не удалось сохранить заявку»).
 */
export function formatApiError(error: unknown, context?: string): string {
  const detail = extractApiErrorDetail(error);
  if (detail && context) {
    if (detail.startsWith(context)) return detail;
    return `${context.replace(/[.!?]\s*$/, '')}. ${detail}`;
  }
  if (detail) return detail;
  if (context) return context;
  return FALLBACK;
}

/** Normalize rejected requests once, including legacy inline-banner consumers. */
export function prepareApiError(error: unknown): void {
  if (!error || typeof error !== 'object') return;
  const e = error as MaybeAxiosError;
  e.apiFieldErrors = extractApiFieldErrors(e);
  const detail = extractApiErrorDetail(e);
  if (!detail) return;
  e.apiErrorMessage = detail;
  e.message = detail;
  if (e.response?.data) {
    e.response.data = {...e.response.data, message: detail};
  }
}
